import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Plus, RotateCcw, X } from "lucide-react";
import { ROLE_LABEL, ROLE_STYLE, day } from "./format";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * One capability, one ordered chain. Each lookup walks it top-to-bottom and
 * stops at the first provider that answers, so position 1 is where the money
 * goes and everything below it is free insurance.
 */
export default function RoutingPanel({ capability, vendorLabels, onSaved }) {
  const [chain, setChain] = useState(capability.order);
  const [busy, setBusy] = useState(false);

  useEffect(() => setChain(capability.order), [capability.order]);

  const dirty = !same(chain, capability.order);
  const off = capability.available.filter((p) => !chain.includes(p));
  const label = (id) => vendorLabels[id] || id;

  const move = (idx, delta) => {
    const next = [...chain];
    const to = idx + delta;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    setChain(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/dashboard/services/providers/routing`, {
        capability: capability.id,
        order: chain,
      });
      toast.success(`${label(chain[0])} is now primary for ${capability.label.toLowerCase()}`);
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the provider order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-200/80 bg-white p-4"
      data-testid={`routing-${capability.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-800">{capability.label}</h4>
          <p className="text-xs text-slate-500">{capability.blurb}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {dirty && (
            <>
              <button
                onClick={() => setChain(capability.order)}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-md"
                data-testid={`routing-${capability.id}-reset`}
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
              <button
                onClick={save}
                disabled={busy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50 transition-colors"
                data-testid={`routing-${capability.id}-save`}
              >
                <Check className="w-3.5 h-3.5" /> Apply order
              </button>
            </>
          )}
          {!dirty && capability.updated_by && (
            <span className="text-[11px] text-slate-400" data-testid={`routing-${capability.id}-meta`}>
              set by {capability.updated_by.split("@")[0]} · {day(capability.updated_at)}
            </span>
          )}
          {!dirty && !capability.updated_by && (
            <span className="text-[11px] text-slate-400">default order</span>
          )}
        </div>
      </div>

      <ol className="space-y-2" data-testid={`routing-${capability.id}-chain`}>
        {chain.map((provider, idx) => (
          <li
            key={provider}
            className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2"
            data-testid={`routing-${capability.id}-row-${provider}`}
          >
            <span className="w-5 text-center text-xs font-bold text-slate-400">{idx + 1}</span>
            <span className="text-sm font-semibold text-slate-800">{label(provider)}</span>
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ROLE_STYLE[idx === 0 ? "primary" : idx === 1 ? "secondary" : "third"]}`}
            >
              {ROLE_LABEL[idx === 0 ? "primary" : idx === 1 ? "secondary" : "third"]}
            </span>
            {(capability.identity_only || []).includes(provider) && (
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200"
                title="Returns vehicle identity only — used for payment links and the website, never for a report"
              >
                Identity only
              </span>
            )}
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="p-1 rounded text-slate-400 hover:text-slate-800 hover:bg-white disabled:opacity-25 disabled:hover:bg-transparent"
                title="Move up"
                data-testid={`routing-${capability.id}-up-${provider}`}
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => move(idx, 1)}
                disabled={idx === chain.length - 1}
                className="p-1 rounded text-slate-400 hover:text-slate-800 hover:bg-white disabled:opacity-25 disabled:hover:bg-transparent"
                title="Move down"
                data-testid={`routing-${capability.id}-down-${provider}`}
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setChain(chain.filter((p) => p !== provider))}
                disabled={chain.length === 1}
                className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-25 disabled:hover:bg-transparent"
                title="Take out of the chain"
                data-testid={`routing-${capability.id}-remove-${provider}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ol>

      {off.length > 0 && (
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`routing-${capability.id}-off`}>
          <span className="text-[11px] text-slate-400">Out of the chain:</span>
          {off.map((provider) => (
            <button
              key={provider}
              onClick={() => setChain([...chain, provider])}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-500 rounded-md border border-dashed border-slate-300 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
              data-testid={`routing-${capability.id}-add-${provider}`}
            >
              <Plus className="w-3 h-3" /> {label(provider)}
            </button>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-400">
        Each lookup tries these in order and stops at the first one that answers — a provider lower
        down costs nothing until the one above it fails.
        {capability.source === "env" && " Currently the default order; applying a change pins it."}
        {(capability.identity_only || []).length > 0 &&
          " An identity-only provider is skipped entirely for report-grade lookups."}
      </p>
    </div>
  );
}
